import { ensureSubscriptionActionAllowed } from '../../utils/subscriptionActionGuard';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import * as XLSX from "xlsx";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, Segmented, EmptyState,
  Fab, Sheet, ActionRow, DetailRow, Field, TextInput, TextAreaInput,
  SelectInput, SwitchRow, Pill, Mono, Ico, fmt, useMobileMenu,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useCommercialTreasuryPermissions } from "../../gestion-tresoreries/useCommercialTreasuryPermissions";
import { useConsolidatedTreasuryPermissions } from "../../gestion-tresoreries/useConsolidatedTreasuryPermissions";
import {
  createTreasuryMovement,
  createTreasuryTransfer,
  deactivateTreasuryAccount,
  fetchTreasuryAccounts,
  fetchTreasuryFlows,
  fetchTreasuryMovements,
  fetchTreasuryTotals,
  saveTreasuryAccount,
} from "./treasuryMobileApi";

const PAGE = 20;
const today = () => new Date().toISOString().slice(0, 10);
const money = (value, currency = "F") =>
  value === null || value === undefined
    ? "—"
    : `${fmt(Number(value) || 0)} ${currency || "F"}`;
const dateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("fr-FR");
};
const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
const numberValue = (value) => {
  const normalized = String(value ?? "").replace(/\s/g, "").replace(",", ".");
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
};

const ACCOUNT_TYPES = [
  { value: "cash", label: "Caisse" },
  { value: "bank", label: "Banque" },
  { value: "mobile", label: "Mobile money" },
];
const MOVEMENT_CATEGORIES = [
  { value: "deposit", label: "Dépôt" },
  { value: "withdrawal", label: "Retrait" },
  { value: "advance", label: "Avance" },
  { value: "refund", label: "Remboursement" },
  { value: "adjustment", label: "Ajustement" },
];
const CATEGORY_LABELS = {
  payment_customer: "Paiement client",
  payment_supplier: "Paiement fournisseur",
  deposit: "Dépôt",
  withdrawal: "Retrait",
  advance: "Avance",
  refund: "Remboursement",
  adjustment: "Ajustement",
  transfer_in: "Transfert entrant",
  transfer_out: "Transfert sortant",
};

const emptyAccountForm = () => ({
  name: "", account_type: "cash", opening_balance: "0", opening_date: today(),
  is_default: false, is_active: true, notes: "",
});
const emptyMovementForm = () => ({
  account_id: "", direction: "in", category: "deposit", amount: "",
  movement_date: today(), reference: "", description: "",
});
const emptyTransferForm = () => ({
  from_account_id: "", to_account_id: "", amount: "", movement_date: today(),
  reference: "", description: "",
});

export default function TreasuryMobile({
  consolidated = false,
  moduleScope: moduleScopeOverride,
  permissions: permissionOverrides,
  title: titleOverride,
  flowLoader = fetchTreasuryFlows,
  totalsLoader = fetchTreasuryTotals,
}) {
  const onMenu = useMobileMenu();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const commercialPermissions = useCommercialTreasuryPermissions();
  const consolidatedPermissions = useConsolidatedTreasuryPermissions();
  const permissions = permissionOverrides || (consolidated ? consolidatedPermissions : commercialPermissions);
  const moduleScope = moduleScopeOverride || (consolidated ? "all" : "commercial");
  const screenTitle = titleOverride || (consolidated ? "Trésorerie consolidée" : "Trésorerie");

  const views = useMemo(() => {
    const items = [];
    if (!consolidated && permissions.canViewTreasury) items.push({ value: "flows", label: "Flux" });
    if (permissions.canViewTreasuryAccounts) items.push({ value: "accounts", label: "Comptes" });
    if (permissions.canViewTreasuryMovements) items.push({ value: "movements", label: "Mouvements" });
    if (!consolidated && permissions.canCreateTreasuryTransfers) items.push({ value: "transfers", label: "Transfert" });
    return items;
  }, [consolidated, permissions]);

  const [view, setView] = useState(consolidated ? "accounts" : "flows");
  const [navOpen, setNavOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const inFlight = useRef(false);

  const [flows, setFlows] = useState([]);
  const [flowTotal, setFlowTotal] = useState(0);
  const [flowPage, setFlowPage] = useState(1);
  const [flowLoading, setFlowLoading] = useState(false);
  const [flowFilters, setFlowFilters] = useState({
    search: "", payment_type: "all", mode_type: "all", start_date: "", end_date: "",
  });
  const [totals, setTotals] = useState({
    incoming: 0,
    outgoing: 0,
    count: 0,
    currency: "F",
    currencyCount: 0,
    balance: null,
    balanceAsOf: null,
    accountCount: 0,
    balanceCurrency: "F",
    balanceCurrencyCount: 0,
  });

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [accountForm, setAccountForm] = useState(emptyAccountForm);
  const [editingAccount, setEditingAccount] = useState(null);

  const [movements, setMovements] = useState([]);
  const [movementTotal, setMovementTotal] = useState(0);
  const [movementPage, setMovementPage] = useState(1);
  const [movementLoading, setMovementLoading] = useState(false);
  const [movementFilters, setMovementFilters] = useState({
    search: "", direction: "all", category: "all", account_id: "", start_date: "", end_date: "",
  });
  const [movementForm, setMovementForm] = useState(emptyMovementForm);
  const [transferForm, setTransferForm] = useState(emptyTransferForm);

  useEffect(() => {
    if (!views.some((item) => item.value === view)) setView(views[0]?.value || "accounts");
  }, [view, views]);

  const baseParams = useCallback(() => ({
    company_id: selectedCompany,
    warehouse_id: selectedWarehouse,
    module_scope: moduleScope,
  }), [moduleScope, selectedCompany, selectedWarehouse]);

  const loadFlows = useCallback(async (page = 1, append = false) => {
    if (!selectedCompany || !selectedWarehouse || consolidated || !permissions.canViewTreasury) return;
    setFlowLoading(true);
    try {
      const params = { ...baseParams(), page, limit: PAGE };
      Object.entries(flowFilters).forEach(([key, value]) => {
        if (value && value !== "all") params[key] = value;
      });
      const result = await flowLoader(params);
      setFlows((prev) => append ? [...prev, ...result.rows] : result.rows);
      setFlowTotal(result.total);
      setFlowPage(page);
      const totalParams = { ...params };
      delete totalParams.page;
      delete totalParams.limit;
      setTotals(await totalsLoader(totalParams));
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les flux de trésorerie"));
      if (!append) setFlows([]);
    } finally {
      setFlowLoading(false);
    }
  }, [baseParams, consolidated, flowFilters, flowLoader, permissions.canViewTreasury, selectedCompany, selectedWarehouse, totalsLoader]);

  const loadAccounts = useCallback(async () => {
    if (!selectedCompany || !selectedWarehouse) return;
    setAccountsLoading(true);
    try {
      const rows = await fetchTreasuryAccounts({
        ...baseParams(), include_inactive: includeInactive,
      });
      setAccounts(rows);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les comptes"));
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [baseParams, includeInactive, selectedCompany, selectedWarehouse]);

  const loadMovements = useCallback(async (page = 1, append = false) => {
    if (!selectedCompany || !selectedWarehouse) return;
    setMovementLoading(true);
    try {
      const params = { ...baseParams(), page, limit: PAGE };
      Object.entries(movementFilters).forEach(([key, value]) => {
        if (value && value !== "all") params[key] = value;
      });
      const result = await fetchTreasuryMovements(params);
      setMovements((prev) => append ? [...prev, ...result.rows] : result.rows);
      setMovementTotal(result.total);
      setMovementPage(page);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les mouvements"));
      if (!append) setMovements([]);
    } finally {
      setMovementLoading(false);
    }
  }, [baseParams, movementFilters, selectedCompany, selectedWarehouse]);

  useEffect(() => {
    if (view === "flows") loadFlows(1, false);
  }, [loadFlows, view]);
  useEffect(() => {
    if (["accounts", "movements", "transfers"].includes(view)) loadAccounts();
  }, [loadAccounts, view]);
  useEffect(() => {
    if (view === "movements") loadMovements(1, false);
  }, [loadMovements, view]);

  const openAccountForm = (account = null) => {
    setEditingAccount(account);
    setAccountForm(account ? {
      name: account.name || "",
      account_type: account.account_type || "cash",
      opening_balance: String(account.opening_balance ?? 0),
      opening_date: account.opening_date ? String(account.opening_date).slice(0, 10) : today(),
      is_default: Number(account.is_default) === 1,
      is_active: Number(account.is_active) === 1,
      notes: account.notes || "",
    } : emptyAccountForm());
    setFormMode("account");
    setDetail(null);
  };

  const submitAccount = async () => {
    if (inFlight.current || !accountForm.name.trim()) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await saveTreasuryAccount(editingAccount?.id, {
        ...baseParams(),
        name: accountForm.name.trim(),
        account_type: accountForm.account_type,
        opening_balance: numberValue(accountForm.opening_balance),
        opening_date: accountForm.opening_date || null,
        is_default: accountForm.is_default,
        is_active: accountForm.is_active,
        notes: accountForm.notes.trim() || null,
      });
      message.success(editingAccount ? "Compte mis à jour" : "Compte créé");
      setFormMode(null);
      setEditingAccount(null);
      await loadAccounts();
    } catch (error) {
      message.error(errorMessage(error, "Impossible d'enregistrer le compte"));
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  };

  const deactivateAccount = (account) => {
    Modal.confirm({
      title: "Désactiver ce compte ?",
      content: account.name,
      okText: "Désactiver",
      okType: "danger",
      cancelText: "Annuler",
      centered: true,
      onOk: async () => {
        try {
          await deactivateTreasuryAccount(account.id);
          message.success("Compte désactivé");
          setDetail(null);
          loadAccounts();
        } catch (error) {
          message.error(errorMessage(error, "Impossible de désactiver le compte"));
        }
      },
    });
  };

  const submitMovement = async () => {
    if (inFlight.current || !movementForm.account_id || numberValue(movementForm.amount) <= 0) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await createTreasuryMovement({
        module_scope: moduleScope,
        account_id: movementForm.account_id,
        direction: movementForm.direction,
        category: movementForm.category,
        amount: numberValue(movementForm.amount),
        movement_date: movementForm.movement_date || undefined,
        reference: movementForm.reference.trim() || undefined,
        description: movementForm.description.trim() || undefined,
      });
      message.success("Mouvement enregistré");
      setFormMode(null);
      setMovementForm(emptyMovementForm());
      await Promise.all([loadMovements(1, false), loadAccounts()]);
    } catch (error) {
      message.error(errorMessage(error, "Impossible d'enregistrer le mouvement"));
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  };

  const submitTransfer = async () => {
    const amount = numberValue(transferForm.amount);
    if (inFlight.current || !transferForm.from_account_id || !transferForm.to_account_id || amount <= 0) return;
    if (String(transferForm.from_account_id) === String(transferForm.to_account_id)) {
      message.warning("Les comptes source et destination doivent être différents");
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      await createTreasuryTransfer({
        module_scope: moduleScope,
        from_account_id: transferForm.from_account_id,
        to_account_id: transferForm.to_account_id,
        amount,
        movement_date: transferForm.movement_date || undefined,
        reference: transferForm.reference.trim() || undefined,
        description: transferForm.description.trim() || undefined,
      });
      message.success("Transfert enregistré");
      setTransferForm(emptyTransferForm());
      await loadAccounts();
    } catch (error) {
      message.error(errorMessage(error, "Impossible d'enregistrer le transfert"));
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  };

  const exportFlows = async () => {
    if (exporting || !permissions.canExportTreasury) return;
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    setExporting(true);
    try {
      const params = { ...baseParams(), page: 1, limit: 10000 };
      Object.entries(flowFilters).forEach(([key, value]) => {
        if (value && value !== "all") params[key] = value;
      });
      const result = await flowLoader(params);
      if (!result.rows.length) {
        message.warning("Aucun flux à exporter");
        return;
      }
      const sheet = XLSX.utils.json_to_sheet(result.rows.map((item) => ({
        Date: dateLabel(item.payment_date),
        Type: item.payment_type === "in" ? "Encaissement" : "Décaissement",
        Tiers: item.entity_name || "",
        Document: item.document_number || "",
        Mode: item.payment_mode_name || "",
        Montant: Number(item.amount || 0),
        Devise: item.currency_symbol || "",
        Référence: item.payment_reference || "",
        "Créé par": item.created_by_name || "",
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Trésorerie");
      XLSX.writeFile(workbook, `Tresorerie_${today()}.xlsx`);
      message.success("Export terminé");
    } catch (error) {
      message.error(errorMessage(error, "Export impossible"));
    } finally {
      setExporting(false);
    }
  };

  if (!selectedCompany || !selectedWarehouse) {
    return (
      <ProductScreen>
        <Appbar title={screenTitle} onMenu={onMenu} />
        <div className="body"><EmptyState icon="banknote" title="Sélection requise" text="Choisissez une entreprise et un établissement." /></div>
      </ProductScreen>
    );
  }

  if (!views.length) {
    return (
      <ProductScreen>
        <Appbar title={screenTitle} onMenu={onMenu} />
        <div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" text="Vous n'avez pas la permission d'afficher la trésorerie." /></div>
      </ProductScreen>
    );
  }

  const currency = totals.currencyCount > 1 ? "" : totals.currency;
  const balanceCurrency =
    totals.balanceCurrencyCount > 1 ? "" : totals.balanceCurrency;
  const accountOptions = accounts.filter((a) => Number(a.is_active) === 1).map((a) => ({ value: String(a.id), label: a.name }));

  const renderFlows = () => (
    <>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[
          { label: "Encaissements", value: money(totals.incoming, currency) },
          { label: "Décaissements", value: money(totals.outgoing, currency) },
          {
            label: totals.balanceAsOf
              ? `Solde au ${dateLabel(totals.balanceAsOf)}`
              : "Solde de trésorerie",
            value:
              totals.balanceCurrencyCount > 1
                ? "Multi-devise"
                : money(totals.balance, balanceCurrency),
          },
        ]} />
        <SearchBar value={flowFilters.search} onChange={(search) => setFlowFilters((f) => ({ ...f, search }))} placeholder="Paiement, tiers, facture…" />
        <Chips value={flowFilters.payment_type} onChange={(payment_type) => setFlowFilters((f) => ({ ...f, payment_type }))} options={[
          { key: "all", label: "Tous" }, { key: "in", label: "Entrées" }, { key: "out", label: "Sorties" },
        ]} />
      </div>
      <div className="body">
        {flowLoading && !flows.length ? <EmptyState loading title="Chargement…" /> : !flows.length ? (
          <EmptyState icon="banknote" title="Aucun flux" text="Aucun mouvement ne correspond aux filtres." />
        ) : (
          <div className="list">
            {flows.map((item) => (
              <button key={item.id} className="rcard" onClick={() => setDetail({ type: "flow", item })}>
                <Mono name={item.entity_name || item.document_number || "T"} />
                <div className="rcard__main">
                  <div className="rcard__name">{item.entity_name || (item.payment_type === "in" ? "Client" : "Fournisseur")}</div>
                  <div className="rcard__meta">
                    <Pill tone={item.payment_type === "in" ? "ok" : "danger"} label={item.payment_type === "in" ? "Encaissement" : "Décaissement"} />
                    <span className="rcard__sub">{dateLabel(item.payment_date)} · {item.payment_mode_name || "Mode inconnu"}</span>
                  </div>
                </div>
                <div className="rcard__price">{money(item.amount, item.currency_symbol)}</div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
            {flows.length < flowTotal ? (
              <button className="btn btn--ghost" onClick={() => loadFlows(flowPage + 1, true)} disabled={flowLoading}>
                {flowLoading ? "Chargement…" : `Charger plus (${flows.length}/${flowTotal})`}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </>
  );

  const renderAccounts = () => (
    <>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[
          { label: "Comptes", value: fmt(accounts.length) },
          { label: "Actifs", value: fmt(accounts.filter((a) => Number(a.is_active) === 1).length) },
          { label: "Par défaut", value: fmt(accounts.filter((a) => Number(a.is_default) === 1).length) },
        ]} />
        <SwitchRow label="Afficher les comptes inactifs" value={includeInactive} onChange={setIncludeInactive} />
      </div>
      <div className="body">
        {accountsLoading && !accounts.length ? <EmptyState loading title="Chargement…" /> : !accounts.length ? (
          <EmptyState icon="banknote" title="Aucun compte" text="Créez une caisse, une banque ou un compte mobile money." />
        ) : (
          <div className="list">
            {accounts.map((account) => (
              <button key={account.id} className="rcard" onClick={() => setDetail({ type: "account", item: account })}>
                <Mono name={account.name} square />
                <div className="rcard__main">
                  <div className="rcard__name">{account.name}</div>
                  <div className="rcard__meta">
                    <Pill tone={Number(account.is_active) === 1 ? "ok" : "off"} label={Number(account.is_active) === 1 ? "Actif" : "Inactif"} />
                    <span className="rcard__sub">{ACCOUNT_TYPES.find((t) => t.value === account.account_type)?.label || account.account_type}</span>
                  </div>
                </div>
                <div className="rcard__price">{money(account.current_balance ?? account.opening_balance, account.currency_symbol)}</div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
          </div>
        )}
      </div>
      {permissions.canCreateTreasuryAccounts ? <Fab label="Nouveau compte" onClick={() => openAccountForm()} /> : null}
    </>
  );

  const renderMovements = () => (
    <>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <SearchBar value={movementFilters.search} onChange={(search) => setMovementFilters((f) => ({ ...f, search }))} placeholder="Référence ou description…" />
        <Chips value={movementFilters.direction} onChange={(direction) => setMovementFilters((f) => ({ ...f, direction }))} options={[
          { key: "all", label: "Tous" }, { key: "in", label: "Entrées" }, { key: "out", label: "Sorties" },
        ]} />
      </div>
      <div className="body">
        {movementLoading && !movements.length ? <EmptyState loading title="Chargement…" /> : !movements.length ? (
          <EmptyState icon="arrow-right-left" title="Aucun mouvement" text="Aucun mouvement ne correspond aux filtres." />
        ) : (
          <div className="list">
            {movements.map((item) => (
              <button key={item.id} className="rcard" onClick={() => setDetail({ type: "movement", item })}>
                <Mono name={item.account_name || "M"} />
                <div className="rcard__main">
                  <div className="rcard__name">{item.account_name || `Compte ${item.account_id}`}</div>
                  <div className="rcard__meta">
                    <Pill tone={item.direction === "in" ? "ok" : "danger"} label={item.direction === "in" ? "Entrée" : "Sortie"} />
                    <span className="rcard__sub">{CATEGORY_LABELS[item.category] || item.category} · {dateLabel(item.movement_date)}</span>
                  </div>
                </div>
                <div className="rcard__price">{money(item.amount, item.currency_symbol)}</div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
            {movements.length < movementTotal ? (
              <button className="btn btn--ghost" onClick={() => loadMovements(movementPage + 1, true)} disabled={movementLoading}>
                {movementLoading ? "Chargement…" : `Charger plus (${movements.length}/${movementTotal})`}
              </button>
            ) : null}
          </div>
        )}
      </div>
      {permissions.canCreateTreasuryMovements ? <Fab label="Nouveau mouvement" onClick={() => { setMovementForm(emptyMovementForm()); setFormMode("movement"); }} /> : null}
    </>
  );

  const renderTransfer = () => (
    <div className="body">
      <div className="list">
        <div className="preview">
          <div className="field__lbl">Transfert interne</div>
          <p style={{ margin: "6px 0 0", color: "var(--muted)", fontSize: 13 }}>
            Le transfert génère automatiquement une sortie et une entrée dans les comptes concernés.
          </p>
        </div>
        <Field label="Compte source *"><SelectInput value={transferForm.from_account_id} onChange={(value) => setTransferForm((f) => ({ ...f, from_account_id: value }))} options={accountOptions} placeholder="Sélectionner" /></Field>
        <Field label="Compte destination *"><SelectInput value={transferForm.to_account_id} onChange={(value) => setTransferForm((f) => ({ ...f, to_account_id: value }))} options={accountOptions} placeholder="Sélectionner" /></Field>
        <Field label="Montant *"><TextInput value={transferForm.amount} onChange={(value) => setTransferForm((f) => ({ ...f, amount: value }))} inputMode="decimal" placeholder="0" /></Field>
        <Field label="Date"><div className="ctrl"><input type="date" value={transferForm.movement_date} onChange={(e) => setTransferForm((f) => ({ ...f, movement_date: e.target.value }))} /></div></Field>
        <Field label="Référence"><TextInput value={transferForm.reference} onChange={(value) => setTransferForm((f) => ({ ...f, reference: value }))} /></Field>
        <Field label="Description"><TextAreaInput value={transferForm.description} onChange={(value) => setTransferForm((f) => ({ ...f, description: value }))} rows={3} /></Field>
        <button className="btn btn--primary" onClick={submitTransfer} disabled={saving || accountOptions.length < 2}>
          <Ico name={saving ? "loader-circle" : "arrow-right-left"} className={saving ? "spin" : ""} />
          {saving ? "Enregistrement…" : "Enregistrer le transfert"}
        </button>
      </div>
    </div>
  );

  return (
    <ProductScreen>
      <Appbar
        title={screenTitle}
        subtitle={view === "flows" ? `${fmt(totals.count)} transactions` : views.find((v) => v.value === view)?.label}
        onMenu={onMenu}
        action={<button className="iconbtn" aria-label="Changer de vue" onClick={() => setNavOpen(true)}><Ico name="list-ordered" /></button>}
      />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Segmented value={view} onChange={setView} options={views} />
        {["flows", "movements"].includes(view) ? (
          <button className="btn btn--ghost" onClick={() => setFiltersOpen(true)}><Ico name="sliders-horizontal" />Filtres avancés</button>
        ) : null}
      </div>

      {view === "flows" ? renderFlows() : null}
      {view === "accounts" ? renderAccounts() : null}
      {view === "movements" ? renderMovements() : null}
      {view === "transfers" ? renderTransfer() : null}

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…">
        {views.map((item) => <ActionRow key={item.value} icon="chevron-right" label={item.label} onClick={() => { setView(item.value); setNavOpen(false); }} />)}
        {!consolidated && permissions.canExportTreasury ? <ActionRow icon="file-text" label={exporting ? "Export en cours…" : "Exporter les flux"} onClick={() => { setNavOpen(false); exportFlows(); }} /> : null}
      </Sheet>

      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filtres avancés">
        {view === "flows" ? (
          <>
            <Field label="Type de mode"><SelectInput value={flowFilters.mode_type} onChange={(mode_type) => setFlowFilters((f) => ({ ...f, mode_type }))} options={[{ value: "all", label: "Tous" }, ...ACCOUNT_TYPES]} /></Field>
            <Field label="Date début"><div className="ctrl"><input type="date" value={flowFilters.start_date} onChange={(e) => setFlowFilters((f) => ({ ...f, start_date: e.target.value }))} /></div></Field>
            <Field label="Date fin"><div className="ctrl"><input type="date" value={flowFilters.end_date} onChange={(e) => setFlowFilters((f) => ({ ...f, end_date: e.target.value }))} /></div></Field>
          </>
        ) : (
          <>
            <Field label="Compte"><SelectInput value={movementFilters.account_id} onChange={(account_id) => setMovementFilters((f) => ({ ...f, account_id }))} options={[{ value: "", label: "Tous" }, ...accountOptions]} /></Field>
            <Field label="Catégorie"><SelectInput value={movementFilters.category} onChange={(category) => setMovementFilters((f) => ({ ...f, category }))} options={[{ value: "all", label: "Toutes" }, ...MOVEMENT_CATEGORIES]} /></Field>
            <Field label="Date début"><div className="ctrl"><input type="date" value={movementFilters.start_date} onChange={(e) => setMovementFilters((f) => ({ ...f, start_date: e.target.value }))} /></div></Field>
            <Field label="Date fin"><div className="ctrl"><input type="date" value={movementFilters.end_date} onChange={(e) => setMovementFilters((f) => ({ ...f, end_date: e.target.value }))} /></div></Field>
          </>
        )}
        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={() => {
            if (view === "flows") setFlowFilters({ search: "", payment_type: "all", mode_type: "all", start_date: "", end_date: "" });
            else setMovementFilters({ search: "", direction: "all", category: "all", account_id: "", start_date: "", end_date: "" });
          }}>Réinitialiser</button>
          <button className="btn btn--primary" onClick={() => setFiltersOpen(false)}>Appliquer</button>
        </div>
      </Sheet>

      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.item?.name || detail?.item?.account_name || detail?.item?.entity_name || "Détail"}>
        {detail?.type === "flow" ? <>
          <DetailRow label="Type" value={detail.item.payment_type === "in" ? "Encaissement" : "Décaissement"} />
          <DetailRow label="Montant" value={money(detail.item.amount, detail.item.currency_symbol)} />
          <DetailRow label="Date" value={dateLabel(detail.item.payment_date)} />
          <DetailRow label="Mode" value={detail.item.payment_mode_name || "—"} />
          <DetailRow label="Document" value={detail.item.document_number || "—"} />
          <DetailRow label="Référence" value={detail.item.payment_reference || "—"} />
          <DetailRow label="Créé par" value={detail.item.created_by_name || "—"} />
        </> : null}
        {detail?.type === "account" ? <>
          <DetailRow label="Type" value={ACCOUNT_TYPES.find((t) => t.value === detail.item.account_type)?.label || detail.item.account_type} />
          <DetailRow label="Solde actuel" value={money(detail.item.current_balance ?? detail.item.opening_balance, detail.item.currency_symbol)} />
          <DetailRow label="Solde d'ouverture" value={money(detail.item.opening_balance, detail.item.currency_symbol)} />
          <DetailRow label="Date d'ouverture" value={dateLabel(detail.item.opening_date)} />
          <DetailRow label="Statut" value={Number(detail.item.is_active) === 1 ? "Actif" : "Inactif"} />
          <DetailRow label="Compte par défaut" value={Number(detail.item.is_default) === 1 ? "Oui" : "Non"} />
          {permissions.canEditTreasuryAccounts ? <ActionRow icon="pencil" label="Modifier" onClick={() => openAccountForm(detail.item)} /> : null}
          {permissions.canDeleteTreasuryAccounts && Number(detail.item.is_active) === 1 ? <ActionRow icon="trash-2" label="Désactiver" danger onClick={() => deactivateAccount(detail.item)} /> : null}
        </> : null}
        {detail?.type === "movement" ? <>
          <DetailRow label="Direction" value={detail.item.direction === "in" ? "Entrée" : "Sortie"} />
          <DetailRow label="Catégorie" value={CATEGORY_LABELS[detail.item.category] || detail.item.category} />
          <DetailRow label="Montant" value={money(detail.item.amount, detail.item.currency_symbol)} />
          <DetailRow label="Solde après mouvement" value={money(detail.item.balance_after, detail.item.currency_symbol)} />
          <DetailRow label="Date" value={dateLabel(detail.item.movement_date)} />
          <DetailRow label="Référence" value={detail.item.reference || "—"} />
          <DetailRow label="Description" value={detail.item.description || "—"} />
        </> : null}
      </Sheet>

      <Sheet open={formMode === "account"} onClose={() => setFormMode(null)} title={editingAccount ? "Modifier le compte" : "Nouveau compte"}>
        <Field label="Nom du compte *"><TextInput value={accountForm.name} onChange={(name) => setAccountForm((f) => ({ ...f, name }))} placeholder="Ex : Caisse principale" /></Field>
        <Field label="Type *"><SelectInput value={accountForm.account_type} onChange={(account_type) => setAccountForm((f) => ({ ...f, account_type }))} options={ACCOUNT_TYPES} /></Field>
        <Field label="Solde d'ouverture"><TextInput value={accountForm.opening_balance} onChange={(opening_balance) => setAccountForm((f) => ({ ...f, opening_balance }))} inputMode="decimal" /></Field>
        <Field label="Date d'ouverture"><div className="ctrl"><input type="date" value={accountForm.opening_date} onChange={(e) => setAccountForm((f) => ({ ...f, opening_date: e.target.value }))} /></div></Field>
        <SwitchRow label="Compte par défaut" value={accountForm.is_default} onChange={(is_default) => setAccountForm((f) => ({ ...f, is_default }))} />
        <SwitchRow label="Compte actif" value={accountForm.is_active} onChange={(is_active) => setAccountForm((f) => ({ ...f, is_active }))} />
        <Field label="Notes"><TextAreaInput value={accountForm.notes} onChange={(notes) => setAccountForm((f) => ({ ...f, notes }))} rows={3} /></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormMode(null)}>Annuler</button><button className="btn btn--primary" onClick={submitAccount} disabled={saving || !accountForm.name.trim()}>{saving ? "Enregistrement…" : editingAccount ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>

      <Sheet open={formMode === "movement"} onClose={() => setFormMode(null)} title="Nouveau mouvement">
        <Field label="Compte *"><SelectInput value={movementForm.account_id} onChange={(account_id) => setMovementForm((f) => ({ ...f, account_id }))} options={accountOptions} placeholder="Sélectionner" /></Field>
        <Field label="Direction *"><SelectInput value={movementForm.direction} onChange={(direction) => setMovementForm((f) => ({ ...f, direction }))} options={[{ value: "in", label: "Entrée" }, { value: "out", label: "Sortie" }]} /></Field>
        <Field label="Catégorie *"><SelectInput value={movementForm.category} onChange={(category) => setMovementForm((f) => ({ ...f, category }))} options={MOVEMENT_CATEGORIES} /></Field>
        <Field label="Montant *"><TextInput value={movementForm.amount} onChange={(amount) => setMovementForm((f) => ({ ...f, amount }))} inputMode="decimal" /></Field>
        <Field label="Date"><div className="ctrl"><input type="date" value={movementForm.movement_date} onChange={(e) => setMovementForm((f) => ({ ...f, movement_date: e.target.value }))} /></div></Field>
        <Field label="Référence"><TextInput value={movementForm.reference} onChange={(reference) => setMovementForm((f) => ({ ...f, reference }))} /></Field>
        <Field label="Description"><TextAreaInput value={movementForm.description} onChange={(description) => setMovementForm((f) => ({ ...f, description }))} rows={3} /></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormMode(null)}>Annuler</button><button className="btn btn--primary" onClick={submitMovement} disabled={saving || !movementForm.account_id || numberValue(movementForm.amount) <= 0}>{saving ? "Enregistrement…" : "Enregistrer"}</button></div>
      </Sheet>
    </ProductScreen>
  );
}
